import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import RichTextEditor from '../../components/ui/RichTextEditor';
import { uploadFileAndGetUrl } from '../../services/coa';
import { createThirdPartyCOA, getThirdPartyCOA, updateThirdPartyCOA } from '../../services/thirdPartyCoa';

const CreateThirdPartyCOA = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [isSaving, setIsSaving] = useState(false);
  const logoInputRef = useRef(null);

  // Core fields
  const [title, setTitle] = useState('');
  const [organization, setOrganization] = useState('');
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState('active');

  const [diseaseOrCondition, setDiseaseOrCondition] = useState('');
  const [descriptionContent, setDescriptionContent] = useState('');
  const [authorsContent, setAuthorsContent] = useState('');
  const [copyrightText, setCopyrightText] = useState('');
  const [permissionsContent, setPermissionsContent] = useState('');
  const [restrictionsContent, setRestrictionsContent] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [tag, setTag] = useState('');

  const [details, setDetails] = useState({
    typeOfCOA: '',
    targetAgeGroup: '',
    respondents: '',
    gender: '',
    numItems: '',
    recallPeriod: '',
    responseScales: '',
    domains: '',
    adminTime: '',
    adminModes: '',
    originalDevelopmentLanguage: '',
  });

  const [ecoaNotesContent, setEcoaNotesContent] = useState('');
  const [otherInfoContent, setOtherInfoContent] = useState('');
  const [keyReferencesContent, setKeyReferencesContent] = useState('');
  const [externalLinksContent, setExternalLinksContent] = useState('');
  const [backgroundContent, setBackgroundContent] = useState('');
  const [scoringSystemContent, setScoringSystemContent] = useState('');
  const [developmentMethodologyContent, setDevelopmentMethodologyContent] = useState('');
  const [exampleStudiesContent, setExampleStudiesContent] = useState('');

  const handleDetailsChange = (name, value) => {
    setDetails(prev => ({ ...prev, [name]: value }));
  };

  // Load existing 3rd party COA in edit mode
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await getThirdPartyCOA(id);
        const d = res?.data;
        if (!d) return;

        setTitle(d.title || '');
        setOrganization(d.organization || '');
        setUrl(d.url || '');
        setStatus(d.status || 'active');

        setDiseaseOrCondition(d.diseaseOrCondition || '');
        setDescriptionContent(d.description || '');
        setAuthorsContent(d.authors || '');
        setCopyrightText(d.copyright || '');
        setPermissionsContent( d.permissionsToUse || '');
        setRestrictionsContent(d.restrictions || '');
        setLogoUrl(d.logoUrl || '');
        setTag(d.tag || '');

        const det = d.details || {};
        setDetails({
          typeOfCOA: det.typeOfCOA || '',
          targetAgeGroup: det.targetAgeGroup || '',
          respondents: det.respondents || '',
          gender: det.gender || '',
          numItems: det.numItems ?? '',
          recallPeriod: det.recallPeriod || '',
          responseScales: det.responseScales || '',
          domains: det.domains || '',
          adminTime: det.adminTime || '',
          adminModes: det.adminModes || '',
          originalDevelopmentLanguage: det.originalDevelopmentLanguage || '',
        });

        setEcoaNotesContent(d.ecoaNotes || '');
        setOtherInfoContent(d.otherInformation || '');
        setKeyReferencesContent(d.references || '');
        setExternalLinksContent(d.externalLinks || '');
        setBackgroundContent(d.background || '');
        setScoringSystemContent(d.scoringSystem || '');
        setDevelopmentMethodologyContent(d.developmentMethodology || '');
        setExampleStudiesContent(d.exampleStudies || '');
      } catch (e) {
        console.error('Failed to load 3rd party COA', e);
      }
    })();
  }, [id]);

  const buildPayload = () => ({
    title: title.trim(),
    organization: organization.trim(),
    url: url.trim(),
    status,
    diseaseOrCondition: diseaseOrCondition || undefined,
    description: descriptionContent || undefined,
    authors: authorsContent || undefined,
    copyright: copyrightText || undefined,
    permissionsToUse: permissionsContent || undefined,
    restrictions: restrictionsContent || undefined,
    ecoaNotes: ecoaNotesContent || undefined,
    otherInformation: otherInfoContent || undefined,
    references: keyReferencesContent || undefined,
    externalLinks: externalLinksContent || undefined,
    background: backgroundContent || undefined,
    scoringSystem: scoringSystemContent || undefined,
    developmentMethodology: developmentMethodologyContent || undefined,
    exampleStudies: exampleStudiesContent || undefined,
    logoUrl: logoUrl || undefined,
    tag: tag || undefined,
    details: {
      typeOfCOA: details.typeOfCOA || undefined,
      targetAgeGroup: details.targetAgeGroup || undefined,
      respondents: details.respondents || undefined,
      gender: details.gender || undefined,
      numItems: details.numItems ? Number(details.numItems) : undefined,
      recallPeriod: details.recallPeriod || undefined,
      responseScales: details.responseScales || undefined,
      domains: details.domains || undefined,
      adminTime: details.adminTime || undefined,
      adminModes: details.adminModes || undefined,
      originalDevelopmentLanguage: details.originalDevelopmentLanguage || undefined,
    },
  });

  const handleSave = async () => {
    const trimmedTitle = title.trim();
    const trimmedOrg = organization.trim();
    const trimmedUrl = url.trim();
    if (!trimmedTitle || !trimmedOrg || !trimmedUrl) {
      alert('Please enter Title, Organization and URL');
      return;
    }
    setIsSaving(true);
    try {
      const payload = buildPayload();
      if (id) {
        await updateThirdPartyCOA(id, payload);
      } else {
        await createThirdPartyCOA(payload);
      }
      navigate('/coa-library');
    } catch (e) {
      console.error('Failed to save 3rd party COA', e);
      alert('Failed to save 3rd party COA. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[28px] font-semibold text-[#0a1f17] tracking-[-0.56px] leading-[1.4]">
            {id ? 'Edit 3rd Party COA' : 'Create 3rd Party COA'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Capture full product details for external / 3rd party COAs.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/coa-library')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            type="button"
          >
            Back to Library
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-5 py-2 bg-gradient-to-r from-[#40c191] to-[#5ed58d] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity shadow-sm disabled:opacity-60"
            type="button"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Main Content - Product Details only */}
      <div className="bg-white rounded-lg border border-gray-200 p-10">
        <h2 className="text-lg font-semibold text-gray-900 mb-8">Product Details</h2>

        {/* Logo */}
        <div className="mb-10">
          <label className="block text-sm font-medium text-gray-700 mb-3">Logo</label>
          <div className="flex items-start gap-4">
            <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center text-white relative flex-shrink-0 overflow-hidden">
              {logoUrl ? (
                <img src={logoUrl} alt="COA Logo" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center text-gray-500 text-xs">No Logo</div>
              )}
            </div>
            <div>
              <div className="flex gap-2 mb-2">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files && e.target.files[0];
                    if (!f) return;
                    try {
                      const uploadedUrl = await uploadFileAndGetUrl(f);
                      setLogoUrl(uploadedUrl);
                    } catch (err) {
                      console.error('Logo upload failed', err);
                      alert('Logo upload failed');
                    } finally {
                      e.target.value = '';
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => logoInputRef.current && logoInputRef.current.click()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-600 rounded-lg text-sm font-medium hover:bg-emerald-100 transition-colors border border-emerald-200"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M8 10.6667V4M8 4L5.33333 6.66667M8 4L10.6667 6.66667"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2.66667 10.6667V12C2.66667 12.3536 2.80714 12.6928 3.05719 12.9428C3.30724 13.1929 3.64638 13.3333 4 13.3333H12C12.3536 13.3333 12.6928 13.1929 12.9428 12.9428C13.1929 12.6928 13.3333 12.3536 13.3333 12V10.6667"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Upload new image
                </button>
                {logoUrl && (
                  <button
                    type="button"
                    onClick={() => setLogoUrl('')}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors border border-gray-300"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M2 4H14M6.66667 7.33333V11.3333M9.33333 7.33333V11.3333M3.33333 4L4 13.3333C4 13.687 4.14048 14.026 4.39052 14.2761C4.64057 14.5262 4.97971 14.6667 5.33333 14.6667H10.6667C11.0203 14.6667 11.3594 14.5262 11.6095 14.2761C11.8595 14.026 12 13.687 12 13.3333L12.6667 4M6 4V2.66667C6 2.48986 6.07024 2.32029 6.19526 2.19526C6.32029 2.07024 6.48986 2 6.66667 2H9.33333C9.51014 2 9.67971 2.07024 9.80474 2.19526C9.92976 2.32029 10 2.48986 10 2.66667V4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Remove
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500">System supports PNG and JPG, under 10MB</p>
            </div>
          </div>
        </div>

        {/* Title / Organization / URL / Disease */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Organization</label>
            <input
              type="text"
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">External URL</label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              placeholder="https://example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Disease / Condition</label>
            <input
              type="text"
              value={diseaseOrCondition}
              onChange={(e) => setDiseaseOrCondition(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent bg-white"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Description */}
        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
          <RichTextEditor
            value={descriptionContent}
            onChange={setDescriptionContent}
            placeholder="Enter description..."
          />
        </div>

        {/* Copyright & Usage Rights */}
        <div className="mb-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">Copyright & Usage Rights</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Authors</label>
              <input
                type="text"
                value={authorsContent}
                onChange={(e) => setAuthorsContent(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Copyright</label>
              <input
                type="text"
                value={copyrightText}
                onChange={(e) => setCopyrightText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Permissions to use / Licence</label>
            <RichTextEditor
              value={permissionsContent}
              onChange={setPermissionsContent}
              placeholder="Enter permissions / licence terms..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Licensing restrictions</label>
            <RichTextEditor
              value={restrictionsContent}
              onChange={setRestrictionsContent}
              placeholder="Enter licensing restrictions..."
            />
          </div>
        </div>

        {/* Details */}
        <div className="mb-10">
          <h3 className="text-base font-semibold text-gray-900 mb-6">Details</h3>
          <div className="grid grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type of COA</label>
              <div className="relative">
                <select
                  value={details.typeOfCOA}
                  onChange={(e) => handleDetailsChange('typeOfCOA', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent bg-white"
                >
                  <option value="">Select</option>
                  <option value="Patient-Reported Outcome Measures (PROMs)">Patient-Reported Outcome Measures (PROMs)</option>
                  <option value="Clinician-Reported Outcome Measures (ClinROs)">Clinician-Reported Outcome Measures (ClinROs)</option>
                  <option value="Observer-Reported Outcome Measures (ObsROs)">Observer-Reported Outcome Measures (ObsROs)</option>
                  <option value="Performance Outcome Measures (PerfOs)">Performance Outcome Measures (PerfOs)</option>
                  <option value="Patient Diary (PD)">Patient Diary (PD)</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M3 4.5L6 7.5L9 4.5"
                      stroke="#9CA3AF"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tag</label>
              <select
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent bg-white"
              >
                <option value="">None</option>
                <option value="new Coa">New COA</option>
                <option value="New Translations">New Translations Available</option>
                <option value="updated">Updated</option>
                <option value="Frequently Requested">Frequently Requested</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Target age group</label>
              <input
                type="text"
                value={details.targetAgeGroup}
                onChange={(e) => handleDetailsChange('targetAgeGroup', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Respondents</label>
              <input
                type="text"
                value={details.respondents}
                onChange={(e) => handleDetailsChange('respondents', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
              <input
                type="text"
                value={details.gender}
                onChange={(e) => handleDetailsChange('gender', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Number of items</label>
              <input
                type="number"
                value={details.numItems}
                onChange={(e) => handleDetailsChange('numItems', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Recall period</label>
              <input
                type="text"
                value={details.recallPeriod}
                onChange={(e) => handleDetailsChange('recallPeriod', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Response scales</label>
              <input
                type="text"
                value={details.responseScales}
                onChange={(e) => handleDetailsChange('responseScales', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Domains / construct measured</label>
              <input
                type="text"
                value={details.domains}
                onChange={(e) => handleDetailsChange('domains', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-6 mb-0">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Administration time</label>
              <input
                type="text"
                value={details.adminTime}
                onChange={(e) => handleDetailsChange('adminTime', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Administration modes</label>
              <input
                type="text"
                value={details.adminModes}
                onChange={(e) => handleDetailsChange('adminModes', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Original development language</label>
              <input
                type="text"
                value={details.originalDevelopmentLanguage}
                onChange={(e) => handleDetailsChange('originalDevelopmentLanguage', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Additional descriptive sections */}
        <div className="grid grid-cols-2 gap-6 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">eCOA Notes</label>
            <RichTextEditor
              value={ecoaNotesContent}
              onChange={setEcoaNotesContent}
              placeholder="Enter eCOA notes..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Additional details</label>
            <RichTextEditor
              value={otherInfoContent}
              onChange={setOtherInfoContent}
              placeholder="Enter other information..."
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Key references</label>
            <RichTextEditor
              value={keyReferencesContent}
              onChange={setKeyReferencesContent}
              placeholder="Enter key references..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">External links</label>
            <RichTextEditor
              value={externalLinksContent}
              onChange={setExternalLinksContent}
              placeholder="One URL per line or comma separated"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Background</label>
            <RichTextEditor
              value={backgroundContent}
              onChange={setBackgroundContent}
              placeholder="Enter background..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Scoring system</label>
            <RichTextEditor
              value={scoringSystemContent}
              onChange={setScoringSystemContent}
              placeholder="Enter scoring system..."
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mt-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Development methodology</label>
            <RichTextEditor
              value={developmentMethodologyContent}
              onChange={setDevelopmentMethodologyContent}
              placeholder="Enter development methodology..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Example studies</label>
            <RichTextEditor
              value={exampleStudiesContent}
              onChange={setExampleStudiesContent}
              placeholder="Enter example studies..."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateThirdPartyCOA;

